import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Filter as FilterIcon, Inbox, Rocket } from 'lucide-react';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import { useAuth } from '../context/AuthContext';
import PersonaSwitcher from './PersonaSwitcher';
import UserProfileMenu from './UserProfileMenu';
import GlobalSearchBar from './search/GlobalSearchBar';
import CommentInbox from './CommentInbox';
import DemoModeModal from './DemoModeModal';
import NotificationBell from './NotificationBell';
import TenantContextIndicator from './TenantContextIndicator';

const TopNavigation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { filters, updateFilter, clearFilters, hasActiveFilters, getFilterParams } = useFilters();
  const [showFilters, setShowFilters] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showDemoMode, setShowDemoMode] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [options, setOptions] = useState({
    portfolios: [],
    programs: [],
    projects: [],
    sprints: [],
    statuses: [],
    teams: [],
  });

  useEffect(() => {
    loadFilterOptions();
    loadInboxCount();
  }, []);

  const loadFilterOptions = async () => {
    try {
      const response = await api.getFilters();
      setOptions({
        ...response.data,
        teams: response.data.teams || []
      });
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  const loadInboxCount = async () => {
    const tenantId = user?.tenant?.id || user?.tenant_id;
    if (!tenantId || !user?.id) return;
    try {
      const res = await api.getCommentInboxCounts({
        tenant_id: tenantId,
        user_id: user.id
      });
      setInboxCount(res.data.counts?.all || 0);
    } catch (err) {
      // Silently fail - inbox is optional
    }
  };

  const handleFilterChange = (key, value) => {
    updateFilter(key, value);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      const filterParams = getFilterParams();
      Object.entries(filterParams).forEach(([key, value]) => {
        params.set(key, value);
      });
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const exportUrl = `${API_URL}/api/export?${params.toString()}`;
      window.open(exportUrl, '_blank');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-[var(--shadow-card)]">
      <div className="px-6">
        {/* Header Row */}
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            {/* Global Search */}
            <GlobalSearchBar className="w-80" />
          </div>

          <div className="flex items-center gap-3">
            {/* Global Tenant Context Indicator - Critical for multi-tenant UX */}
            <TenantContextIndicator />
            
            {/* Sprint 20: Notification Bell */}
            <NotificationBell />
            
            {/* Inbox Button */}
            <button
              onClick={() => setShowInbox(true)}
              className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="My Inbox"
              data-testid="inbox-button"
            >
              <Inbox className="w-5 h-5 text-gray-600" />
              {inboxCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--clr-brand-primary)] text-white text-xs rounded-full flex items-center justify-center">
                  {inboxCount > 9 ? '9+' : inboxCount}
                </span>
              )}
            </button>
            
            {/* Persona Switcher */}
            <PersonaSwitcher />
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-[var(--clr-brand-primary)] text-[var(--clr-text-on-brand)] border border-[var(--clr-brand-primary)]'
                  : 'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-primary)] border border-gray-200 hover:bg-gray-200'
              }`}
              data-testid="toggle-filters"
            >
              <FilterIcon className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 w-2 h-2 bg-white rounded-full"></span>
              )}
            </button>
            
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[var(--clr-brand-primary)] rounded-lg hover:bg-[var(--clr-brand-primary-hover)] transition-colors"
              data-testid="export-button"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            
            {/* Demo Mode Button */}
            <button
              onClick={() => setShowDemoMode(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
              data-testid="demo-mode-button"
              title="Demo Mode"
            >
              <Rocket className="w-4 h-4" />
              Demo
            </button>
            
            {/* User Profile Menu */}
            <div className="ml-2 pl-2 border-l border-gray-200">
              <UserProfileMenu />
            </div>
          </div>
        </div>

        {/* Filters Row */}
        {showFilters && (
          <div className="py-3 border-t border-gray-100">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-[var(--clr-text-muted)] uppercase tracking-wider">Global Filters:</span>
              
              <select
                value={filters.portfolio_id}
                onChange={(e) => handleFilterChange('portfolio_id', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
                data-testid="filter-portfolio"
              >
                <option value="">All Portfolios</option>
                {options.portfolios.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              <select
                value={filters.program_id}
                onChange={(e) => handleFilterChange('program_id', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
                data-testid="filter-program"
              >
                <option value="">All Programs</option>
                {options.programs
                  .filter(p => !filters.portfolio_id || p.portfolio_id === filters.portfolio_id)
                  .map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </select>

              <select
                value={filters.project_id}
                onChange={(e) => handleFilterChange('project_id', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
                data-testid="filter-project"
              >
                <option value="">All Projects</option>
                {options.projects
                  .filter(p => !filters.program_id || p.program_id === filters.program_id)
                  .map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </select>

              <select
                value={filters.sprint_id}
                onChange={(e) => handleFilterChange('sprint_id', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
                data-testid="filter-sprint"
              >
                <option value="">All Sprints</option>
                {options.sprints.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>

              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-white"
                data-testid="filter-status"
              >
                <option value="">All Statuses</option>
                {options.statuses.map((s) => (
                  <option key={s.status} value={s.status}>{s.status}</option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-[var(--clr-brand-primary)] hover:text-blue-700 font-medium ml-2"
                  data-testid="clear-filters"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Comment Inbox Panel */}
      <CommentInbox 
        isOpen={showInbox} 
        onClose={() => { setShowInbox(false); loadInboxCount(); }} 
      />
      
      {/* Demo Mode Modal */}
      <DemoModeModal
        isOpen={showDemoMode}
        onClose={() => setShowDemoMode(false)}
      />
    </div>
  );
};

export default TopNavigation;
